import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  collectionMapOnNameWithoutActions,
  collectionMapWithoutActions,
  getCollectionWithoutActionsSearchRequests,
  getSearch,
} from './search';
import type {
  Hit,
  ActionDocument,
  FormattedSearchResult,
} from './search.types';
import { useGetTypeSenseAPIKeys } from '@/api/REST/typeSenseAPIKeys';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { routes } from '@/constants/routes';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';

const SEARCH_KEY = 'TYPESENSE_SEARCH';

export const getActionUrl = (
  collectionName: string,
  id: number,
  hasNewProjectManagement: boolean = false,
) => {
  switch (collectionName) {
    case 'customers': {
      return legacyRoutes.customers.details(id);
    }
    case 'employees': {
      return legacyRoutes.employees.details(id);
    }
    case 'projects': {
      return hasNewProjectManagement
        ? routes.projects.planning(id)
        : legacyRoutes.projectManagement.index(id);
    }
    case 'invoices': {
      return `/Financial/Invoicing/Edit/${id.toString()}`;
    }
    case 'contacts': {
      return `/CRM/Contact/Edit/${id.toString()}`;
    }
    case 'dashboards': {
      return routes.reporting.dashboard(id);
    }
    default: {
      return '';
    }
  }
};

export const getDocumentWithMappedHighlights = (hit: Hit) => {
  if (hit.highlights && hit.highlights.length > 0) {
    // Create a shallow clone to avoid mutating the original document
    const clonedDocument = { ...hit.document };
    hit.highlights.forEach((highlight) => {
      clonedDocument[highlight.field] = highlight.snippets ?? highlight.snippet;
    });
    return clonedDocument;
  }
  return hit.document;
};

export const formatSearchResultsList = (
  hits: Hit[],
  hasNewProjectManagement: boolean = false,
) => {
  const searchResult: FormattedSearchResult[] = [];
  hits.forEach((hit) => {
    const document = getDocumentWithMappedHighlights(hit);
    const tmp = {
      ...document,
      highlightedProperties: hit.highlights.map((highlight) => highlight.field),
      pageUrl:
        hit.collection === 'actions'
          ? (hit.document as ActionDocument).Url
          : getActionUrl(
              hit.collection,
              document['SourceId'],
              hasNewProjectManagement,
            ),
      score: parseInt(hit?.text_match_info.score ?? '0', 10) || 0,
      documentType: hit.collection,
      raw: hit,
    };
    if (tmp.score && tmp.score !== 0 && tmp.score >= 0.01) {
      searchResult.push(tmp);
    } else {
      return;
    }
  });

  return {
    searchResult,
    highestScore: searchResult.reduce(
      (max, item) => Math.max(max, item['score'] || 0),
      0,
    ),
    isSearchResultEmpty: searchResult.length === 0,
  };
};

export const useGoToResultSearch = (
  searchValue: string,
  perPage: number = 20,
) => {
  const { data: apiKeys, isError: isKeyError } = useGetTypeSenseAPIKeys();
  const hasNewProjectManagement = useHasNewProjectManagement();

  const { data, status, fetchStatus, ...restProps } = useQuery({
    queryKey: [SEARCH_KEY, 'goToResults', searchValue, apiKeys, perPage],
    queryFn: () =>
      getSearch(
        searchValue,
        apiKeys!,
        getCollectionWithoutActionsSearchRequests,
        collectionMapOnNameWithoutActions,
        perPage,
      ),
    enabled: !!searchValue && !isKeyError && !!apiKeys,
    placeholderData: keepPreviousData,
  });

  const { searchResult, isSearchResultEmpty } = (data &&
    'hits' in data &&
    formatSearchResultsList(data.hits, hasNewProjectManagement)) || {
    searchResult: [],
    isSearchResultEmpty: true,
  };

  return {
    searchResult,
    isSearchResultEmpty,
    data,
    status,
    fetchStatus,
    ...restProps,
  };
};

export const useQuickResultSearch = (
  searchValue: string,
  perPage: number = 20,
) => {
  const { data: apiKeys, isError: isKeyError } = useGetTypeSenseAPIKeys();

  const { data, status, fetchStatus, ...restProps } = useQuery({
    queryKey: [SEARCH_KEY, 'quickResults', searchValue, apiKeys, perPage],
    queryFn: () =>
      getSearch(
        searchValue,
        apiKeys!,
        getCollectionWithoutActionsSearchRequests,
        collectionMapWithoutActions,
        perPage,
      ),
    enabled: !!searchValue && !isKeyError && !!apiKeys,
    placeholderData: keepPreviousData,
  });

  const { searchResult, isSearchResultEmpty } = (data &&
    'hits' in data &&
    formatSearchResultsList(data.hits)) || {
    searchResult: [],
    isSearchResultEmpty: true,
  };

  return {
    searchResult,
    isSearchResultEmpty,
    data,
    status,
    fetchStatus,
    ...restProps,
  };
};
